import apiClient from './api/apiClient.js';

class OnRampService {
  /**
   * Get quotes from all providers (Onramper-style format)
   * Uses the consolidated GET /quotes endpoint
   * @param {Object} params - Quote parameters { fiatCurrency, cryptoCurrency, fiatAmount, network, isBuyOrSell }
   * @returns {Promise<Array>} Array of quotes from all providers
   */
  async getAllQuotes(params) {
    const isSell = (params.isBuyOrSell || 'BUY').toUpperCase() === 'SELL';
    const queryParams = {
      source: isSell ? params.cryptoCurrency : params.fiatCurrency,
      destination: isSell ? params.fiatCurrency : params.cryptoCurrency,
      amount: isSell ? (params.cryptoAmount || params.amount || params.fiatAmount) : (params.fiatAmount || params.amount),
      network: params.network,
      type: isSell ? 'sell' : 'buy',
      paymentMethod: params.paymentMethod,
      ramp: params.ramp,
    };
    // Remove undefined values
    Object.keys(queryParams).forEach(key =>
      queryParams[key] === undefined && delete queryParams[key]
    );
    const searchParams = new URLSearchParams(queryParams);
    return apiClient.get(`/api/onramp/quotes?${searchParams.toString()}`);
  }

  /**
   * Compare quotes from all providers with metadata
   * Uses GET /quotes endpoint and transforms response
   * @param {Object} params - Quote parameters
   * @returns {Promise<Object>} AggregatedQuoteResponse with quotes and meta
   */
  async compareQuotes(params) {
    const isSell = (params.isBuyOrSell || 'BUY').toUpperCase() === 'SELL';
    const quotes = await this.getAllQuotes(params);

    // Transform Onramper-style quotes to comparison format
    const successfulQuotes = (quotes || []).filter(q => !q.errors);
    const failedQuotes = (quotes || []).filter(q => q.errors);

    // Find the best quote (highest payout)
    const bestQuote = successfulQuotes.length > 0
      ? successfulQuotes.reduce((best, q) =>
          (q.payout || 0) > (best.payout || 0) ? q : best
        )
      : null;

    return {
      quotes: successfulQuotes.map(q => ({
        provider: q.ramp,
        providerDisplayName: this._formatProviderName(q.ramp),
        fiatAmount: isSell ? q.payout : params.fiatAmount,
        cryptoAmount: isSell ? (params.cryptoAmount || params.fiatAmount) : q.payout,
        payoutAmount: q.payout,
        exchangeRate: q.rate,
        fees: {
          network: q.networkFee || 0,
          service: q.transactionFee || 0,
          total: (q.networkFee || 0) + (q.transactionFee || 0),
        },
        estimatedDeliveryMinutes: q.estimatedDeliveryMinutes,
        widgetSupported: q.widgetSupported,
        recommendations: q.recommendations || [],
        availablePaymentMethods: q.availablePaymentMethods || [],
      })),
      bestQuote: bestQuote ? {
        provider: bestQuote.ramp,
        payoutAmount: bestQuote.payout,
        cryptoAmount: isSell ? (params.cryptoAmount || params.fiatAmount) : bestQuote.payout,
        fiatAmount: isSell ? bestQuote.payout : params.fiatAmount,
      } : null,
      meta: {
        requestedAt: new Date().toISOString(),
        fiatCurrency: params.fiatCurrency,
        cryptoCurrency: params.cryptoCurrency,
        network: params.network,
        fiatAmount: params.fiatAmount,
        cryptoAmount: params.cryptoAmount,
        isBuyOrSell: isSell ? 'SELL' : 'BUY',
        providersQueried: (quotes || []).map(q => q.ramp),
        providersResponded: successfulQuotes.map(q => q.ramp),
        providersFailed: failedQuotes.map(q => q.ramp),
        failureErrors: failedQuotes.flatMap(q => q.errors || []),
      },
    };
  }

  /**
   * Format provider name for display
   * @private
   */
  _formatProviderName(provider) {
    const names = {
      transak: 'Transak',
      banxa: 'Banxa',
      'onramp-money': 'Onramp.money',
      'alchemy-pay': 'Alchemy Pay',
      onmeta: 'OnMeta',
      moonpay: 'MoonPay',
      ramp: 'Ramp Network',
    };
    return names[provider?.toLowerCase()] || provider;
  }
}

export default new OnRampService();
